import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaComments,
  FaExclamationTriangle,
  FaTicketAlt,
} from 'react-icons/fa';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { graphqlRequest } from '../../utils/graphqlRequest';
import { SUPPORT_TICKETS_QUERY } from '../../graphql/supportTicket';
import { createChatSocket } from '../../utils/chatSocket';

const LIVE_CHAT_ACCESS_QUERY = `
  query GetLiveChatAccess {
    getMyProfile {
      company {
        liveChatEnabled
        liveChatSupporterId
      }
    }
  }
`;

function normalizeStatus(s) {
  const v = String(s || '').toUpperCase();
  if (v === 'OPEN') return 'open';
  if (v === 'IN_PROGRESS') return 'progress';
  if (v === 'RESOLVED') return 'resolved';
  return String(s || '').toLowerCase();
}

function normalizePriority(p) {
  const v = String(p || '').toUpperCase();
  if (v === 'URGENT') return 'urgent';
  if (v === 'HIGH') return 'high';
  if (v === 'NORMAL') return 'medium';
  if (v === 'LOW') return 'low';
  return String(p || '').toLowerCase();
}

function normalizeRoleForPie(r) {
  const s = String(r || '').toLowerCase();
  if (s.includes('cleaner')) return 'cleaner';
  if (s.includes('contractor')) return 'contractor';
  return 'manager';
}

function isSameLocalDay(iso, refDate) {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    return (
      d.getFullYear() === refDate.getFullYear() &&
      d.getMonth() === refDate.getMonth() &&
      d.getDate() === refDate.getDate()
    );
  } catch {
    return false;
  }
}

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Last N calendar months ending this month, oldest first */
function lastNMonthKeys(n) {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(monthKey(d));
  }
  return out;
}

function SparkLine({ points, color = '#7c3aed' }) {
  if (!points.length) return null;
  const w = 280;
  const h = 120;
  const pad = 8;
  const max = Math.max(...points, 1);
  const min = 0;
  const span = Math.max(max - min, 1);
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (1 - (v - min) / span) * (h - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg className="support-dashboard__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={coords.join(' ')} />
      {points.map((v, i) => {
        const x = pad + i * step;
        const y = pad + (1 - (v - min) / span) * (h - pad * 2);
        return <circle key={i} cx={x} cy={y} r="4" fill="#fff" stroke={color} strokeWidth="2" />;
      })}
    </svg>
  );
}

function HorizBars({ items, color = '#7c3aed' }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="support-dashboard__hbar-list">
      {items.map((row) => (
        <div key={row.label} className="support-dashboard__hbar-row">
          <span className="support-dashboard__hbar-label">{row.label}</span>
          <div className="support-dashboard__hbar-track">
            <div
              className="support-dashboard__hbar-fill"
              style={{ width: `${(row.value / max) * 100}%`, background: color }}
            />
          </div>
          <span className="support-dashboard__hbar-val">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function VertBars({ items, color = '#1570ef' }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="support-dashboard__vbar-wrap">
      {items.map((row) => (
        <div key={row.label} className="support-dashboard__vbar-col">
          <div className="support-dashboard__vbar-stack">
            <div
              className="support-dashboard__vbar-fill"
              style={{ height: `${(row.value / max) * 100}%`, background: color }}
            />
          </div>
          <span className="support-dashboard__vbar-label" title={row.label}>
            {row.label}
          </span>
          <span className="support-dashboard__vbar-val">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function RolePie({ cleaner, contractor, manager }) {
  const total = cleaner + contractor + manager || 1;
  const pClean = (cleaner / total) * 100;
  const pCont = (contractor / total) * 100;
  const pMan = (manager / total) * 100;
  const c1 = pClean;
  const c2 = c1 + pCont;
  const gradient = `conic-gradient(
    #ec4899 0% ${c1}%,
    #7c3aed ${c1}% ${c2}%,
    #1570ef ${c2}% 100%
  )`;
  return (
    <div className="support-dashboard__pie-wrap">
      <div className="support-dashboard__pie" style={{ background: gradient }} />
      <ul className="support-dashboard__pie-legend">
        <li>
          <span className="support-dashboard__pie-dot" style={{ background: '#ec4899' }} />
          Cleaners <strong>{Math.round(pClean)}%</strong>
        </li>
        <li>
          <span className="support-dashboard__pie-dot" style={{ background: '#7c3aed' }} />
          Contractors <strong>{Math.round(pCont)}%</strong>
        </li>
        <li>
          <span className="support-dashboard__pie-dot" style={{ background: '#1570ef' }} />
          Managers <strong>{Math.round(pMan)}%</strong>
        </li>
      </ul>
    </div>
  );
}

function ResolutionTrendLine() {
  const points = [3.2, 2.9, 2.7, 2.4];
  const w = 640;
  const h = 160;
  const pad = 24;
  const maxY = 3.5;
  const minY = 0;
  const spanY = maxY - minY;
  const step = (w - pad * 2) / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (1 - (v - minY) / spanY) * (h - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg className="support-dashboard__trend-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" aria-hidden>
      {[0, 1, 2, 3].map((i) => {
        const y = pad + (i / 3) * (h - pad * 2);
        return (
          <line key={i} x1={pad} y1={y} x2={w - pad} y2={y} stroke="#e4e7ec" strokeWidth="1" />
        );
      })}
      <polyline
        fill="none"
        stroke="#079455"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={coords.join(' ')}
      />
      {points.map((v, i) => {
        const x = pad + i * step;
        const y = pad + (1 - (v - minY) / spanY) * (h - pad * 2);
        return <circle key={i} cx={x} cy={y} r="5" fill="#fff" stroke="#079455" strokeWidth="2" />;
      })}
      <text x={pad} y={h - 4} fill="#667085" fontSize="11">
        Week 1
      </text>
      <text x={w / 2 - 20} y={h - 4} fill="#667085" fontSize="11">
        Week 2
      </text>
      <text x={w - pad - 36} y={h - 4} fill="#667085" fontSize="11">
        Week 4
      </text>
    </svg>
  );
}

function isManagementRole(roles = []) {
  if (!Array.isArray(roles)) return false;
  return roles.some((role) => {
    const normalized = String(role).toLowerCase().replace(/[\s_/-]+/g, '');
    return (
      normalized.includes('admin') ||
      normalized.includes('manager') ||
      normalized.includes('supervisor') ||
      normalized.includes('operationsmanager')
    );
  });
}

function getConversationPartner(conversation, currentUserId) {
  if (!conversation || conversation.type !== 'DIRECT') return null;
  const members = Array.isArray(conversation.members) ? conversation.members : [];
  return members.find((member) => member?.id && member.id !== currentUserId) || null;
}

function isConversationActive(conversation) {
  const s = String(conversation?.status || '').toLowerCase();
  if (!s) return !conversation?.closedAt;
  return !(s.includes('closed') || s.includes('resolved'));
}

export default function SupportDashboardPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [liveChatCount, setLiveChatCount] = useState(0);
  const [liveChatLoading, setLiveChatLoading] = useState(true);
  const [, setLiveChatError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await graphqlRequest(SUPPORT_TICKETS_QUERY);
      setTickets(data?.supportTickets || []);
    } catch (e) {
      setLoadError(e?.message || 'Could not load support metrics');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let mounted = true;
    const token = Cookies.get('accessToken');

    if (!token) {
      setLiveChatLoading(false);
      setLiveChatCount(0);
      return () => {};
    }

    const decode = () => {
      try {
        const decoded = jwtDecode(token);
        return decoded?.sub?.userId ? String(decoded.sub.userId) : '';
      } catch {
        return '';
      }
    };

    const currentUserId = decode();
    if (!currentUserId) {
      setLiveChatLoading(false);
      setLiveChatCount(0);
      return () => {};
    }

    const socket = createChatSocket(token);

    const loadCount = () => {
      if (!socket) return;
      socket.emit('getMyConversations', {}, (response = {}) => {
        if (!mounted) return;
        const conversations = Array.isArray(response?.conversations)
          ? response.conversations
          : [];

        const activeRows = conversations
          .map((conv) => {
            const partner = getConversationPartner(conv, currentUserId);
            if (!partner) return null;
            if (isManagementRole(partner.roles || [])) return null;
            if (!isConversationActive(conv)) return null;
            return conv;
          })
          .filter(Boolean);

        setLiveChatCount(activeRows.length);
        setLiveChatLoading(false);
      });
    };

    (async () => {
      setLiveChatLoading(true);
      setLiveChatError('');
      try {
        const data = await graphqlRequest(LIVE_CHAT_ACCESS_QUERY);
        const company = data?.getMyProfile?.company;
        const enabled = Boolean(company?.liveChatEnabled);
        const supporterId = String(company?.liveChatSupporterId || '').trim();
        const ok = enabled && supporterId && supporterId.toLowerCase() === currentUserId.toLowerCase();

        if (!ok) {
          setLiveChatCount(0);
          setLiveChatLoading(false);
          return;
        }

        socket.on('connect', () => loadCount());
        socket.on('conversation.created', () => loadCount());
        socket.on('notification.message', () => loadCount());

        // Initial load
        loadCount();
      } catch (e) {
        setLiveChatError(e?.message || 'Failed to load live chat count');
        setLiveChatCount(0);
        setLiveChatLoading(false);
      }
    })();

    return () => {
      mounted = false;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, []);

  const today = useMemo(() => new Date(), []);

  const metrics = useMemo(() => {
    const open = tickets.filter((t) => normalizeStatus(t.status) === 'open').length;
    const inProgress = tickets.filter((t) => normalizeStatus(t.status) === 'progress').length;
    const resolvedToday = tickets.filter(
      (t) => normalizeStatus(t.status) === 'resolved' && isSameLocalDay(t.updatedAt, today),
    ).length;
    const urgent = tickets.filter((t) => normalizePriority(t.priority) === 'urgent').length;
    const total = tickets.length;
    const resolvedAll = tickets.filter((t) => normalizeStatus(t.status) === 'resolved').length;
    const resolutionRate = total ? Math.round((resolvedAll / total) * 1000) / 10 : 0;

    const monthKeys = lastNMonthKeys(6);
    const byMonth = monthKeys.map((mk) => ({
      key: mk,
      count: tickets.filter((t) => {
        if (!t.createdAt) return false;
        const d = new Date(t.createdAt);
        return monthKey(d) === mk;
      }).length,
    }));

    const priOrder = ['urgent', 'high', 'medium', 'low'];
    const priLabels = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
    const priorityBars = priOrder.map((k) => ({
      label: priLabels[k],
      value: tickets.filter((t) => normalizePriority(t.priority) === k).length,
    }));

    const catMap = {};
    tickets.forEach((t) => {
      const c = String(t.category || 'other').trim() || 'other';
      catMap[c] = (catMap[c] || 0) + 1;
    });
    const catEntries = Object.entries(catMap).sort((a, b) => b[1] - a[1]);
    const categoryBars = catEntries.slice(0, 8).map(([label, value]) => ({
      label: label.replace(/-/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase()),
      value,
    }));

    let cleaner = 0;
    let contractor = 0;
    let manager = 0;
    tickets.forEach((t) => {
      const r = normalizeRoleForPie(t.submittedByRole);
      if (r === 'cleaner') cleaner += 1;
      else if (r === 'contractor') contractor += 1;
      else manager += 1;
    });

    return {
      open,
      inProgress,
      resolvedToday,
      urgent,
      total,
      resolutionRate,
      monthSeries: byMonth.map((m) => m.count),
      monthLabels: byMonth.map((m) => {
        const [y, mo] = m.key.split('-');
        return `${MONTH_LABELS[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
      }),
      priorityBars,
      categoryBars,
      rolePie: { cleaner, contractor, manager },
    };
  }, [tickets, today]);

  return (
    <div className="support-dashboard support-dashboard--full">
      <header className="support-dashboard__header">
        <h1>Support Dashboard</h1>
        <p>Overview of support activity across the platform.</p>
        {loadError ? <p className="support-dashboard__error">{loadError}</p> : null}
      </header>

      <div className="support-dashboard__kpi-grid">
        <article className="support-dashboard__kpi">
          <div className="support-dashboard__kpi-top">
            <span className="support-dashboard__kpi-icon support-dashboard__kpi-icon--blue">
              <FaTicketAlt aria-hidden />
            </span>
            <span className="support-dashboard__kpi-value">{loading ? '—' : metrics.open}</span>
          </div>
          <span className="support-dashboard__kpi-label">Open Tickets</span>
        </article>
        <article className="support-dashboard__kpi">
          <div className="support-dashboard__kpi-top">
            <span className="support-dashboard__kpi-icon support-dashboard__kpi-icon--amber">
              <FaClock aria-hidden />
            </span>
            <span className="support-dashboard__kpi-value">{loading ? '—' : metrics.inProgress}</span>
          </div>
          <span className="support-dashboard__kpi-label">In Progress</span>
        </article>
        <article className="support-dashboard__kpi">
          <div className="support-dashboard__kpi-top">
            <span className="support-dashboard__kpi-icon support-dashboard__kpi-icon--green">
              <FaCheckCircle aria-hidden />
            </span>
            <span className="support-dashboard__kpi-value">{loading ? '—' : metrics.resolvedToday}</span>
          </div>
          <span className="support-dashboard__kpi-label">Resolved Today</span>
          <span className="support-dashboard__kpi-trend support-dashboard__kpi-trend--up">↑ 12%</span>
        </article>
        <article className="support-dashboard__kpi">
          <div className="support-dashboard__kpi-top">
            <span className="support-dashboard__kpi-icon support-dashboard__kpi-icon--red">
              <FaExclamationTriangle aria-hidden />
            </span>
            <span className="support-dashboard__kpi-value">{loading ? '—' : metrics.urgent}</span>
          </div>
          <span className="support-dashboard__kpi-label">Urgent Issues</span>
        </article>
        <article className="support-dashboard__kpi">
          <div className="support-dashboard__kpi-top">
            <span className="support-dashboard__kpi-icon support-dashboard__kpi-icon--violet">
              <FaChartLine aria-hidden />
            </span>
            <span className="support-dashboard__kpi-value">2.4h</span>
          </div>
          <span className="support-dashboard__kpi-label">Avg Response Time</span>
          <span className="support-dashboard__kpi-trend support-dashboard__kpi-trend--up">↑ 0.3h</span>
        </article>
        <article className="support-dashboard__kpi">
          <div className="support-dashboard__kpi-top">
            <span className="support-dashboard__kpi-icon support-dashboard__kpi-icon--pink">
              <FaComments aria-hidden />
            </span>
            <span className="support-dashboard__kpi-value">
              {liveChatLoading ? '—' : liveChatCount}
            </span>
          </div>
          <span className="support-dashboard__kpi-label">Active Live Chats</span>
        </article>
      </div>

      <section className="support-dashboard__section">
        <h2 className="support-dashboard__section-title">Support Analytics</h2>
        <p className="support-dashboard__section-sub">Insights and trends for support activity.</p>
        <div className="support-dashboard__analytics-row">
          <article className="support-dashboard__stat">
            <span className="support-dashboard__stat-dot" style={{ background: '#7c3aed' }} />
            <div>
              <span className="support-dashboard__stat-label">Total Tickets</span>
              <span className="support-dashboard__stat-value">{loading ? '—' : metrics.total}</span>
            </div>
            <span className="support-dashboard__stat-delta support-dashboard__stat-delta--up">
              <FaArrowUp aria-hidden /> 8.2%
            </span>
          </article>
          <article className="support-dashboard__stat">
            <span className="support-dashboard__stat-dot" style={{ background: '#079455' }} />
            <div>
              <span className="support-dashboard__stat-label">Avg Resolution Time</span>
              <span className="support-dashboard__stat-value">2.4h</span>
            </div>
            <span className="support-dashboard__stat-delta support-dashboard__stat-delta--down">
              <FaArrowDown aria-hidden /> 12.5%
            </span>
          </article>
          <article className="support-dashboard__stat">
            <span className="support-dashboard__stat-dot" style={{ background: '#1570ef' }} />
            <div>
              <span className="support-dashboard__stat-label">Resolution Rate</span>
              <span className="support-dashboard__stat-value">
                {loading ? '—' : `${metrics.resolutionRate}%`}
              </span>
            </div>
            <span className="support-dashboard__stat-delta support-dashboard__stat-delta--up">
              <FaArrowUp aria-hidden /> 3.1%
            </span>
          </article>
          <article className="support-dashboard__stat">
            <span className="support-dashboard__stat-dot" style={{ background: '#f59e0b' }} />
            <div>
              <span className="support-dashboard__stat-label">Satisfaction Score</span>
              <span className="support-dashboard__stat-value">4.7/5</span>
            </div>
            <span className="support-dashboard__stat-delta support-dashboard__stat-delta--up">
              <FaArrowUp aria-hidden /> 0.3
            </span>
          </article>
        </div>
      </section>

      <div className="support-dashboard__charts-grid">
        <article className="support-dashboard__chart-card">
          <h3 className="support-dashboard__chart-title">Monthly Support Volume</h3>
          <div className="support-dashboard__chart-xlabels">
            {metrics.monthLabels.map((lb) => (
              <span key={lb}>{lb}</span>
            ))}
          </div>
          <SparkLine points={metrics.monthSeries.length ? metrics.monthSeries : [0, 0, 0, 0, 0, 0]} />
        </article>
        <article className="support-dashboard__chart-card">
          <h3 className="support-dashboard__chart-title">Tickets by Priority</h3>
          <HorizBars items={metrics.priorityBars} />
        </article>
        <article className="support-dashboard__chart-card">
          <h3 className="support-dashboard__chart-title">Tickets by Category</h3>
          {metrics.categoryBars.length ? (
            <VertBars items={metrics.categoryBars} />
          ) : (
            <p className="support-dashboard__empty">No category data yet.</p>
          )}
        </article>
        <article className="support-dashboard__chart-card">
          <h3 className="support-dashboard__chart-title">Tickets by User Role</h3>
          {metrics.total ? (
            <RolePie
              cleaner={metrics.rolePie.cleaner}
              contractor={metrics.rolePie.contractor}
              manager={metrics.rolePie.manager}
            />
          ) : (
            <p className="support-dashboard__empty">No tickets yet.</p>
          )}
        </article>
      </div>

      <article className="support-dashboard__chart-card support-dashboard__chart-card--wide">
        <h3 className="support-dashboard__chart-title">Resolution Time Trend</h3>
        <p className="support-dashboard__chart-sub">Avg Resolution Time (hours) — illustrative trend</p>
        <ResolutionTrendLine />
      </article>
    </div>
  );
}
